import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './UserList.css';

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('nb-NO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('nb-NO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const filterSubscriptionStatuses = (statusesString) => {
    if (!statusesString) return [];

    const statuses = statusesString.split(',').map((s) => s.trim().toLowerCase());
    const filtered = [...statuses];

    if (filtered.includes('active') && filtered.includes('pending')) {
        filtered.splice(filtered.indexOf('pending'), 1);
    }
    if (filtered.includes('trialing') && filtered.includes('pending')) {
        filtered.splice(filtered.indexOf('pending'), 1);
    }
    if (filtered.includes('active') && filtered.includes('canceled')) {
        filtered.splice(filtered.indexOf('canceled'), 1);
    }

    return statusesString
        .split(',')
        .map((s) => s.trim())
        .filter((status) => filtered.includes(status.toLowerCase()));
};

const getActiveSubscriptionCount = (statusesString) => {
    const statuses = filterSubscriptionStatuses(statusesString);
    return statuses.filter((status) => {
        const lower = status.toLowerCase();
        return lower === 'active' || lower === 'trialing';
    }).length;
};

const getUserSortValue = (row, field) => {
    switch (field) {
        case 'name':
            return (row.name || row.email || '').toLowerCase();
        case 'level':
            return row.level_idlevel ?? -1;
        case 'device':
            return (row.device || 'unknown').toLowerCase();
        case 'activeSubscriptions':
            return getActiveSubscriptionCount(row.subscriptionStatuses);
        case 'subscription': {
            const statuses = filterSubscriptionStatuses(row.subscriptionStatuses);
            return statuses.length ? statuses[0].toLowerCase() : '';
        }
        case 'activities':
            return row.totalCarriedOutCount ?? 0;
        case 'program':
            return row.activeProgramCount ?? 0;
        case 'last_logged_in':
            return row.last_logged_in ? new Date(row.last_logged_in).getTime() : 0;
        case 'created_at':
            return row.created_at ? new Date(row.created_at).getTime() : 0;
        case 'businessName':
            return (row.businessName || '').toLowerCase();
        default:
            return '';
    }
};

const getDevicePillClass = (device) => {
    const normalized = (device || '').toLowerCase();
    if (normalized === 'android') return 'user-list-pill--device-android';
    if (normalized === 'ios') return 'user-list-pill--device-ios';
    return 'user-list-pill--device-unknown';
};

export default function UserList() {
    const [users, setUsers] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    const [deviceFilter, setDeviceFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [levelFilter, setLevelFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [createdFrom, setCreatedFrom] = useState('');
    const [createdTo, setCreatedTo] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    useEffect(() => {
        fetchUserRole();
        fetchUsers();
    }, []);

    const fetchUserRole = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`,
                { withCredentials: true }
            );
            setUserRole(response.data.role);
        } catch (err) {
            console.error('Error fetching user role:', err);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/users`,
                { withCredentials: true }
            );
            setUsers(response.data);
            setError('');
        } catch (err) {
            console.error('Error fetching users:', err);
            if (err.response?.status === 401) {
                logout();
            } else {
                setError('Kunne ikke laste brukere');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUserClick = (userId) => {
        navigate(`/users/${userId}`);
    };

    const isSuperAdmin = userRole === 'superadmin';

    const levelOptions = useMemo(() => {
        const map = new Map();
        let hasUnset = false;

        users.forEach((u) => {
            if (u.level_idlevel) {
                map.set(u.level_idlevel, u.levelName || `Nivå ${u.level_idlevel}`);
            } else {
                hasUnset = true;
            }
        });

        const options = Array.from(map.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([id, name]) => ({ id: String(id), name }));

        if (hasUnset) {
            options.push({ id: 'unset', name: 'Ikke satt' });
        }

        return options;
    }, [users]);

    const filteredUsers = useMemo(() => users.filter((row) => {
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const nameMatch = row.name?.toLowerCase().includes(searchLower);
            const emailMatch = row.email?.toLowerCase().includes(searchLower);
            const phoneMatch = row.phone_number?.toLowerCase().includes(searchLower);
            if (!nameMatch && !emailMatch && !phoneMatch) return false;
        }

        if (createdFrom || createdTo) {
            if (!row.created_at) return false;
            const createdDate = new Date(row.created_at);
            if (Number.isNaN(createdDate.getTime())) return false;
            if (createdFrom) {
                const fromDate = new Date(`${createdFrom}T00:00:00`);
                if (createdDate < fromDate) return false;
            }
            if (createdTo) {
                const toDate = new Date(`${createdTo}T23:59:59.999`);
                if (createdDate > toDate) return false;
            }
        }

        if (deviceFilter !== 'all') {
            const device = row.device || 'unknown';
            if (device !== deviceFilter) return false;
        }

        if (levelFilter !== 'all') {
            if (levelFilter === 'unset') {
                if (row.level_idlevel) return false;
            } else if (String(row.level_idlevel) !== levelFilter) {
                return false;
            }
        }

        if (statusFilter !== 'all') {
            if (statusFilter === 'no-subscription') {
                if (row.subscriptionStatuses) return false;
            } else {
                if (!row.subscriptionStatuses) return false;
                const userStatuses = row.subscriptionStatuses
                    .split(',')
                    .map((s) => s.trim().toLowerCase());
                if (!userStatuses.includes(statusFilter.toLowerCase())) return false;
            }
        }

        return true;
    }), [
        users,
        searchTerm,
        createdFrom,
        createdTo,
        deviceFilter,
        levelFilter,
        statusFilter,
    ]);

    const sortedUsers = useMemo(() => {
        const list = [...filteredUsers];
        list.sort((a, b) => {
            const aVal = getUserSortValue(a, sortBy);
            const bVal = getUserSortValue(b, sortBy);
            let cmp = 0;

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                cmp = aVal - bVal;
            } else {
                cmp = String(aVal).localeCompare(String(bVal), 'nb', {
                    numeric: true,
                    sensitivity: 'base',
                });
            }

            return sortOrder === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [filteredUsers, sortBy, sortOrder]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) {
            return <i className="fas fa-sort user-list-sort-icon user-list-sort-icon--inactive" aria-hidden="true" />;
        }
        return (
            <i
                className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} user-list-sort-icon`}
                aria-hidden="true"
            />
        );
    };

    const renderSortableHeader = (field, label) => (
        <th
            className="user-list-th-sortable"
            onClick={() => handleSort(field)}
            aria-sort={
                sortBy === field
                    ? sortOrder === 'asc'
                        ? 'ascending'
                        : 'descending'
                    : 'none'
            }
        >
            <span className="user-list-th-sortable-inner">
                {label}
                {getSortIcon(field)}
            </span>
        </th>
    );

    const deviceCounts = users.reduce((counts, row) => {
        const device = row.device || 'unknown';
        counts[device] = (counts[device] || 0) + 1;
        return counts;
    }, {});

    const androidCount = deviceCounts.android || 0;
    const iosCount = deviceCounts.ios || 0;

    if (loading) {
        return <div className="user-list-loading">Laster brukere…</div>;
    }

    return (
        <div className="user-list">
            <div className="user-list-container">
                <div className="user-list-top">
                    <Link to="/dashboard" className="user-list-back">
                        <i className="fas fa-arrow-left" aria-hidden="true" />
                        Tilbake til hovedsiden
                    </Link>
                    {user?.businessAdminFor && (
                        <Link
                            to="/businesses/current"
                            className="user-list-back user-list-back--primary"
                        >
                            <i className="fas fa-building" aria-hidden="true" />
                            Gå til min bedrift
                        </Link>
                    )}
                </div>

                <header className="user-list-header">
                    <div className="user-list-header-text">
                        <h1>{isSuperAdmin ? 'Alle brukere' : 'Brukere i din bedrift'}</h1>
                        <p>Oversikt over brukere med nivå, abonnement og aktivitet.</p>
                    </div>
                    <button type="button" className="user-list-logout" onClick={logout}>
                        <i className="fas fa-sign-out-alt" aria-hidden="true" />
                        Logg ut
                    </button>
                </header>

                {error && <div className="user-list-error">{error}</div>}

                <div className="user-list-stats">
                    <span className="user-list-stat">
                        <i className="fab fa-android" aria-hidden="true" />
                        Android: {androidCount}
                    </span>
                    <span className="user-list-stat">
                        <i className="fab fa-apple" aria-hidden="true" />
                        iOS: {iosCount}
                    </span>
                    <span className="user-list-stat">
                        <i className="fas fa-users" aria-hidden="true" />
                        Totalt: {users.length}
                    </span>
                    <span className="user-list-stat">
                        <i className="fas fa-filter" aria-hidden="true" />
                        Vist: {filteredUsers.length}
                    </span>
                </div>

                <div className="user-list-filters">
                    <div className="user-list-filter user-list-filter--wide">
                        <label htmlFor="searchTerm">Søk</label>
                        <input
                            type="search"
                            id="searchTerm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Navn, e-post eller telefon…"
                        />
                    </div>

                    <div className="user-list-filter">
                        <label htmlFor="levelFilter">Nivå</label>
                        <select
                            id="levelFilter"
                            value={levelFilter}
                            onChange={(e) => setLevelFilter(e.target.value)}
                        >
                            <option value="all">Alle nivåer</option>
                            {levelOptions.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="user-list-filter">
                        <label htmlFor="deviceFilter">Enhet</label>
                        <select
                            id="deviceFilter"
                            value={deviceFilter}
                            onChange={(e) => setDeviceFilter(e.target.value)}
                        >
                            <option value="all">Alle enheter</option>
                            <option value="android">Android</option>
                            <option value="ios">iOS</option>
                            <option value="unknown">Ukjent</option>
                        </select>
                    </div>

                    <div className="user-list-filter">
                        <label htmlFor="statusFilter">Abonnement</label>
                        <select
                            id="statusFilter"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">Alle statuser</option>
                            <option value="active">Aktiv</option>
                            <option value="trialing">Prøveperiode</option>
                            <option value="pending">Venter</option>
                            <option value="canceled">Kansellert</option>
                            <option value="past_due">Forfalt</option>
                            <option value="unpaid">Ubetalt</option>
                            <option value="no-subscription">Ingen abonnement</option>
                        </select>
                    </div>

                    <div className="user-list-filter">
                        <label htmlFor="createdFrom">Registrert fra</label>
                        <input
                            type="date"
                            id="createdFrom"
                            value={createdFrom}
                            onChange={(e) => setCreatedFrom(e.target.value)}
                        />
                    </div>

                    <div className="user-list-filter">
                        <label htmlFor="createdTo">Registrert til</label>
                        <input
                            type="date"
                            id="createdTo"
                            value={createdTo}
                            onChange={(e) => setCreatedTo(e.target.value)}
                        />
                    </div>
                </div>

                {sortedUsers.length > 0 ? (
                    <div className="user-list-table-wrap">
                        <table className="user-list-table">
                            <thead>
                                <tr>
                                    {renderSortableHeader('name', 'Bruker')}
                                    {renderSortableHeader('level', 'Nivå')}
                                    {renderSortableHeader('device', 'Enhet')}
                                    {renderSortableHeader('activeSubscriptions', 'Aktive abonn.')}
                                    {renderSortableHeader('subscription', 'Abonnement')}
                                    {renderSortableHeader('activities', 'Aktiviteter')}
                                    {renderSortableHeader('program', 'Program')}
                                    {renderSortableHeader('last_logged_in', 'Sist innlogget')}
                                    {renderSortableHeader('created_at', 'Opprettet')}
                                    {isSuperAdmin && renderSortableHeader('businessName', 'Bedrift')}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedUsers.map((row) => {
                                    const filteredStatuses = filterSubscriptionStatuses(
                                        row.subscriptionStatuses
                                    );
                                    const activeCount = getActiveSubscriptionCount(
                                        row.subscriptionStatuses
                                    );
                                    const deviceLabel = row.device
                                        ? row.device.toLowerCase()
                                        : 'Ukjent';

                                    return (
                                        <tr
                                            key={row.idusers}
                                            className="user-list-row"
                                            onClick={() => handleUserClick(row.idusers)}
                                        >
                                            <td>
                                                <span className="user-list-name">
                                                    {row.name || '—'}
                                                </span>
                                                <span className="user-list-email">
                                                    {row.email || '—'}
                                                </span>
                                                {row.phone_number && (
                                                    <span className="user-list-phone">
                                                        {row.phone_number}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className="user-list-pill user-list-pill--level">
                                                    {row.levelName || 'Ikke satt'}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`user-list-pill ${getDevicePillClass(row.device)}`}
                                                >
                                                    {deviceLabel}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="user-list-num">
                                                    {activeCount}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="user-list-statuses">
                                                    {filteredStatuses.length > 0 ? (
                                                        filteredStatuses.map((status) => (
                                                            <span
                                                                key={status}
                                                                className={`user-list-status ${status.trim().toLowerCase()}`}
                                                            >
                                                                {status.trim()}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="user-list-status user-list-status--none">
                                                            Ingen
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="user-list-num">
                                                    {row.totalCarriedOutCount ?? 0}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="user-list-num">
                                                    {row.activeProgramCount ?? 0}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="user-list-date">
                                                    {formatDateTime(row.last_logged_in)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="user-list-date">
                                                    {formatDate(row.created_at)}
                                                </span>
                                            </td>
                                            {isSuperAdmin && (
                                                <td>
                                                    {row.businessName || '—'}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="user-list-empty">
                        Ingen brukere funnet med valgte filtre.
                    </p>
                )}
            </div>
        </div>
    );
}
